import React from 'react';

import {
  ArrowUpRight,
  BarChart3,
  CreditCard,
  LayoutDashboard,
  ListChecks,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import AdminSidebarProfileV2 from '@/components/v2/AdminSidebarProfileV2';
import AdminSidebarV2 from '@/components/v2/AdminSidebarV2';
import AdminTopBarV2 from '@/components/v2/AdminTopBarV2';
import { SIDEBAR_W, SIDEBAR_COLLAPSED_W } from '@/components/v2/defaults';
import type { SidebarItem } from '@/components/v2/types';
import { getAdminAppUrl, getConsoleAppUrl } from '@/config/runtimeUrls';
import { useAuth } from '@/contexts/AuthContext';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { requestAuthHandoff } from '@/services/auth/handoffService';
import { adminPath } from '@/utils/adminPaths';
import { ControlPlaneVariant } from '@company/shared-ui';

type LayoutTarget = 'admin' | 'console';
const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const withPath = (base: string, path: string): string => `${base}${path.startsWith('/') ? path : `/${path}`}`;

const getSidebarConfig = (appTarget: LayoutTarget): { items: SidebarItem[]; sections: Array<{ title: string; labels: string[] }> } => {
  const consoleBase = getConsoleAppUrl();
  const adminBase = getAdminAppUrl();

  if (appTarget === 'admin') {
    return {
      items: [
        { label: 'System', icon: <Settings className="h-4 w-4" />, path: '/system' },
        { label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" />, path: '/dashboard' },
        { label: 'Workers / Queue Health', icon: <ListChecks className="h-4 w-4" />, path: '/workers' },
        { label: 'Security', icon: <ShieldCheck className="h-4 w-4" />, path: '/security' },
        { label: 'MFA Settings', icon: <ShieldCheck className="h-4 w-4" />, path: '/settings/mfa' },
        { label: 'Audit Log', icon: <ListChecks className="h-4 w-4" />, path: '/audit-log' },
        {
          label: 'Tenant Management',
          icon: <ArrowUpRight className="h-4 w-4" />,
          path: '/tenants',
          handoffTargetHost: new URL(consoleBase).host,
          handoffReturnPath: '/tenants',
        },
        {
          label: 'Billing & Credits',
          icon: <ArrowUpRight className="h-4 w-4" />,
          path: '/credits',
          handoffTargetHost: new URL(consoleBase).host,
          handoffReturnPath: '/credits',
        },
        {
          label: 'Analytics',
          icon: <ArrowUpRight className="h-4 w-4" />,
          path: '/analytics',
          handoffTargetHost: new URL(consoleBase).host,
          handoffReturnPath: '/analytics',
        },
      ],
      sections: [
        { title: 'PLATFORM', labels: ['System', 'Dashboard', 'Workers / Queue Health', 'Security', 'MFA Settings', 'Audit Log'] },
        { title: 'OPERATIONS (opens Console)', labels: ['Tenant Management', 'Billing & Credits', 'Analytics'] },
      ],
    };
  }

  return {
    items: [
      { label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" />, path: '/dashboard' },
      { label: 'Tenant Management', icon: <Users className="h-4 w-4" />, path: '/tenants' },
      { label: 'Billing', icon: <CreditCard className="h-4 w-4" />, path: '/billing' },
      { label: 'Credits', icon: <CreditCard className="h-4 w-4" />, path: '/credits' },
      { label: 'Analytics', icon: <BarChart3 className="h-4 w-4" />, path: '/analytics' },
      {
        label: 'System',
        icon: <ArrowUpRight className="h-4 w-4" />,
        path: '/system',
        handoffTargetHost: new URL(adminBase).host,
        handoffReturnPath: '/system',
      },
      {
        label: 'Security',
        icon: <ArrowUpRight className="h-4 w-4" />,
        path: '/security',
        handoffTargetHost: new URL(adminBase).host,
        handoffReturnPath: '/security',
      },
    ],
    sections: [
      { title: 'OPERATIONS', labels: ['Dashboard', 'Tenant Management', 'Billing', 'Credits', 'Analytics'] },
      { title: 'PLATFORM (opens Admin)', labels: ['System', 'Security'] },
    ],
  };
};

const AdminLayout: React.FC<{ appTarget: LayoutTarget }> = ({ appTarget }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useDarkMode();
  const { user } = useAuth();
  const { items: sidebarItems, sections: sidebarSections } = getSidebarConfig(appTarget);
  const isAdmin = Boolean(
    (user && String(user?.systemRole || user?.role || '').toLowerCase().includes('admin')) ||
      user?.is_internal_admin
  );

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  const handleSidebarCollapsedChange = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  const activePath =
    location.pathname === '/tenants/new'
      ? '/tenants/new'
      : location.pathname.startsWith('/tenants/')
      ? '/tenants'
      : location.pathname;

  const handleCrossHostNavigate = React.useCallback(
    async (item: SidebarItem) => {
      if (!item.handoffTargetHost) {
        if (item.externalHref) {
          window.location.replace(item.externalHref);
          return;
        }
        navigate(adminPath(item.path));
        return;
      }

      try {
        const destination = await requestAuthHandoff(
          item.handoffTargetHost,
          item.handoffReturnPath || item.path || '/dashboard'
        );
        window.location.href = destination.redirectUrl;
      } catch (error: any) {
        const params = new URLSearchParams({
          source: appTarget,
          targetHost: item.handoffTargetHost,
          returnPath: location.pathname || '/dashboard',
        });
        if (error?.message) {
          params.set('reason', String(error.message));
        }
        navigate(adminPath(`/handoff-error?${params.toString()}`));
      }
    },
    [appTarget, location.pathname, navigate]
  );

  React.useEffect(() => {
    const root = document.documentElement;
    root.dataset.surface = 'control-plane';
    const mode = theme === 'dark' ? ControlPlaneVariant.dark : ControlPlaneVariant.light;
    root.style.setProperty('--control-plane-primary', ControlPlaneVariant.primary);
    root.style.setProperty('--control-plane-accent', ControlPlaneVariant.accent);
    root.style.setProperty('--control-plane-background', ControlPlaneVariant.background);
    root.style.setProperty('--control-plane-active-bg', mode.activeBg);
    root.style.setProperty('--control-plane-border', mode.border);
    root.style.setProperty('--control-plane-text', mode.text);
    root.style.setProperty('--control-plane-ring', ControlPlaneVariant.focusRing);
  }, [theme]);

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .grid-layout {
            grid-template-columns: 0 1fr !important;
          }
        }

        .grid-layout {
          transition: grid-template-columns 300ms ease-in-out;
        }
      `}</style>
      <div
        className="min-h-screen grid theme-2025 grid-layout"
        style={{ gridTemplateColumns: `${currentSidebarWidth}px 1fr` }}
      >
        <aside className="border-r border-neutral-200 dark:border-neutral-700 bg-background/60 dark:bg-neutral-900/60 hidden md:block transition-colors duration-300">
          <AdminSidebarV2
            items={sidebarItems}
            sections={sidebarSections}
            activePath={activePath}
            onNavigate={(p: string) => navigate(adminPath(p))}
            onCrossHostNavigate={handleCrossHostNavigate}
            isAdmin={isAdmin}
            isCollapsed={sidebarCollapsed}
            onCollapsedChange={handleSidebarCollapsedChange}
            footer={{
              title: appTarget === 'admin' ? 'Platform Surface' : 'Operations Surface',
              description:
                appTarget === 'admin'
                  ? 'Infrastructure and control-plane actions.'
                  : 'Operator workflows and tenant operations.',
              collapsedIcon: appTarget === 'admin' ? 'PF' : 'OP',
            }}
          />
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-[80%] max-w-[300px] bg-white shadow-xl">
              <div className="h-16 border-b border-slate-200" />
              <div className="p-3">
                <AdminSidebarProfileV2
                  name={user?.name || 'Admin'}
                  role={user?.systemRole || user?.role || 'Admin'}
                  email={user?.email || ''}
                  isAdmin={isAdmin}
                  onSettings={() => {
                    navigate(adminPath('/settings/mfa'));
                    setSidebarOpen(false);
                  }}
                />
                <nav className="mt-3 space-y-1">
                  {sidebarItems.map((it) =>
                    it.handoffTargetHost ? (
                      <button
                        key={`${it.label}-${it.path}`}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          void handleCrossHostNavigate(it);
                          setSidebarOpen(false);
                        }}
                      >
                        <span className="text-brand-600">{it.icon}</span>
                        <span>{it.label}</span>
                      </button>
                    ) : it.externalHref ? (
                      <a
                        key={`${it.label}-${it.path}`}
                        href={it.externalHref}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <span className="text-brand-600">{it.icon}</span>
                        <span>{it.label}</span>
                      </a>
                    ) : (
                      <button
                        key={it.path}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          navigate(adminPath(it.path));
                          setSidebarOpen(false);
                        }}
                      >
                        <span className="text-brand-600">{it.icon}</span>
                        <span>{it.label}</span>
                      </button>
                    )
                  )}
                </nav>
              </div>
            </div>
          </div>
        )}

        <main className="w-full h-screen flex flex-col bg-white dark:bg-neutral-900 text-slate-900 dark:text-neutral-100 overflow-hidden transition-colors duration-300">
          <div
            className="flex-shrink-0"
            style={{ height: 'calc(4rem + var(--internal-banner-offset, 0px))' }}
          >
            <AdminTopBarV2
              onBurger={() => setSidebarOpen(true)}
              onSearch={(q?: string) => {
                const query = (q || '').trim();
                if (query) {
                  navigate(adminPath(`/dashboard?query=${encodeURIComponent(query)}`));
                }
              }}
              isSidebarCollapsed={sidebarCollapsed}
              title={appTarget === 'admin' ? 'Platform Control Plane' : 'Operations Console'}
            />
          </div>
          <div className="flex-1 overflow-y-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminLayout;
